import clsx from "clsx";
import { CardForm } from "components/CardForm";
import { IconText } from "components/IconText";
import { RewriteStorySaveButton } from "components/buttons/RewriteStorySaveButton";
import { GlobalContext } from "contexts/GlobalContext";
import { useAutoGrowTextArea } from "hooks/useAutoGrowTextArea";
import { useCopyStory } from "hooks/useCopyStory";
import { useServerForm } from "hooks/useServerForm";
import { Fragment, useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useWatch } from "react-hook-form";
import { CompletedGamePlayerStateResult } from "results/frankenstories/CompletedGamePlayerStateResult";
import { GetGameStateResult } from "results/frankenstories/GetGameStateResult";

interface Rewrite {
    promptText: string;
}

export const RewriteStory = (props: {
    gameState?: GetGameStateResult;
    playerState?: CompletedGamePlayerStateResult;
}) => {
    const { gameState, playerState } = props;

    const { addNotification } = useContext(GlobalContext);

    const { register, control, setValue } = useServerForm<Rewrite>();

    const values = useWatch({ control });

    const { promptText } = values;

    const { copiedStory } = useCopyStory({ gameState });
    const { handleTextAreaChange, textAreaHolderRef } = useAutoGrowTextArea({ promptText });

    const [isSaved, setIsSaved] = useState<boolean>();

    // initial load of value
    useEffect(() => {
        if (gameState && playerState) {
            // set initial text
            if (playerState.revisedStory) {
                setValue("promptText", playerState.revisedStory);
                setIsSaved(true);
            } else {
                setValue("promptText", copiedStory);
                setIsSaved(false);
            }
        }
    }, [copiedStory, gameState, playerState, setValue]);

    if (!gameState || !playerState) {
        return null;
    }

    return (
        <div className="vstack">
            <CardForm
                footer={
                    <div className="hstack gap-3 justify-content-between flex-grow-1">
                        <div>
                            {playerState?.isPlayer && (
                                <RewriteStorySaveButton
                                    code={gameState.code}
                                    promptText={promptText}
                                    onSaved={() => setIsSaved(true)}
                                    isSaved={!!isSaved}
                                />
                            )}
                        </div>

                        <div className="hstack gap-2">
                            <button
                                type="button"
                                className="btn btn-dark icon-link"
                                onClick={() => {
                                    if (promptText) {
                                        navigator.clipboard.writeText(promptText);

                                        addNotification({
                                            title: "Your version copied",
                                            description: "Now go paste it somewhere!",
                                            type: "success",
                                        });
                                    }
                                }}
                            >
                                <IconText icon="fas fa-copy" text="Copy my version" />
                            </button>

                            <button
                                type="button"
                                className="btn btn-dark icon-link"
                                onClick={() => {
                                    const myWindow = window.open("", "", "height: 500; width: 500");

                                    if (myWindow) {
                                        ReactDOM.render(
                                            <Fragment>
                                                <link
                                                    rel="stylesheet"
                                                    href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
                                                    integrity="sha384-T3c6CoIi6uLrA9TneNEoa7RxnatzjcDSCmG1MXxSR1GAsXEV/Dwwykc2MPK8M2HN"
                                                    crossOrigin="anonymous"
                                                />
                                                <pre>{promptText}</pre>
                                            </Fragment>,
                                            myWindow.document.body,
                                        );

                                        myWindow.document.title = gameState.title ?? gameState.gameName;
                                        myWindow.document.close();
                                        myWindow.focus();
                                        myWindow.print();
                                        myWindow.close();
                                    }
                                }}
                            >
                                <IconText icon="fas fa-print" text="Print" />
                            </button>
                        </div>
                    </div>
                }
            >
                <div className="vstack gap-3">
                    {playerState?.isPlayer && (
                        <div
                            className={clsx(
                                "alert text-white mb-0",
                                isSaved && "bg-success",
                                isSaved === false && "bg-primary",
                            )}
                        >
                            <div className="hstack gap-3">
                                <div className="fs-2">
                                    <span
                                        className={clsx("fas", isSaved ? "fa-check" : "fa-triangle-exclamation")}
                                    ></span>
                                </div>

                                <div className="vstack align-self-center">
                                    <div className="h6">This version is {isSaved ? "saved" : "not saved"}</div>

                                    {isSaved ? (
                                        <div>
                                            Click <strong>Update</strong> to submit any further changes
                                        </div>
                                    ) : (
                                        <div>
                                            Click <strong>Save</strong> to submit any changes
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="vstack gap-2">
                        <div className="grow-wrap" ref={textAreaHolderRef}>
                            <textarea
                                id="rewrite"
                                {...register("promptText", {
                                    onChange: handleTextAreaChange,
                                })}
                            ></textarea>
                        </div>
                    </div>
                </div>
            </CardForm>
        </div>
    );
};
